import React, { useState } from 'react';
import { Modal, Heading, Text } from './ui';
import photoCapture from '../services/photoCapture';
import { uploadImage, getRecipeById, updateRecipe } from '../services/api';
import { useAuth } from '../context/authContext';
import sessionReportService from '../services/sessionReportService';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a'
};

const ReflectionModal = ({
    isOpen,
    onClose,
    onSkip,
    onFinishNoPost,
    onPost,
    steps = [],
    sessionId,
    user,
    recipeId = null,
    isRecipe = false
}) => {
    const [stepReflections, setStepReflections] = useState(() => {
        // Initialize with empty reflections for each step
        return steps.map((step, index) => ({
            stepIndex: index,
            photos: [],
            notes: ''
        }));
    });
    const [finalThoughts, setFinalThoughts] = useState('');
    const [finalPhoto, setFinalPhoto] = useState(null);
    const [finalPhotoPreview, setFinalPhotoPreview] = useState(null);
    const [addingToStep, setAddingToStep] = useState(null); // Track which step is being edited
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleAddPhotoToStep = async (stepIndex) => {
        try {
            const blob = await photoCapture.capturePhotoWithUI();
            const compressedBlob = await photoCapture.compressImage(blob, 1024, 0.8);
            const base64 = await photoCapture.blobToBase64(compressedBlob);
            
            // Upload photo to get URL
            if (user && sessionId) {
                const token = await user.getIdToken();
                const imageFile = new File([compressedBlob], `reflection_photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
                const imageUrl = await uploadImage(imageFile, token);
                
                setStepReflections(prev => {
                    const updated = [...prev];
                    updated[stepIndex] = {
                        ...updated[stepIndex],
                        photos: [...(updated[stepIndex].photos || []), { url: imageUrl, base64 }]
                    };
                    return updated;
                });
            }
        } catch (error) {
            console.error('Error capturing photo:', error);
            if (!error.message?.includes('cancelled')) {
                alert('Failed to capture photo. Please try again.');
            }
        }
    };

    const handleAddTextToStep = (stepIndex) => {
        setAddingToStep(stepIndex);
    };

    const handleStepNotesChange = (stepIndex, notes) => {
        setStepReflections(prev => {
            const updated = [...prev];
            updated[stepIndex] = {
                ...updated[stepIndex],
                notes
            };
            return updated;
        });
        setAddingToStep(null);
    };

    const handleFinalPhotoCapture = async () => {
        try {
            const blob = await photoCapture.capturePhotoWithUI();
            const compressedBlob = await photoCapture.compressImage(blob, 1024, 0.8);
            const base64 = await photoCapture.blobToBase64(compressedBlob);
            const previewUrl = URL.createObjectURL(compressedBlob);
            
            // Upload photo to get URL
            if (user && sessionId) {
                const token = await user.getIdToken();
                const imageFile = new File([compressedBlob], `final_photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
                const imageUrl = await uploadImage(imageFile, token);
                
                setFinalPhoto({ base64, previewUrl, url: imageUrl });
            } else {
                setFinalPhoto({ base64, previewUrl });
            }
        } catch (error) {
            console.error('Error capturing final photo:', error);
            if (!error.message?.includes('cancelled')) {
                alert('Failed to capture photo. Please try again.');
            }
        }
    };

    const handleRemoveFinalPhoto = () => {
        if (finalPhoto?.previewUrl) {
            URL.revokeObjectURL(finalPhoto.previewUrl);
        }
        setFinalPhoto(null);
    };

    const handleSkip = () => {
        onSkip();
        onClose();
    };

    const saveReflectionData = async () => {
        if (!sessionId || !user) return;
        
        const token = await user.getIdToken();
        let currentReport;
        
        try {
            // Get current report
            const reportResult = await sessionReportService.getSessionReport(sessionId, token);
            currentReport = reportResult?.data || reportResult;
        } catch (error) {
            // If report doesn't exist, create a new one with all required fields
            if (error.status === 404) {
                const now = new Date().toISOString();
                currentReport = {
                    session_id: sessionId,
                    user_id: user.uid,
                    start_time: now,
                    timeline: [],
                    highlights: [],
                    final_outcome: {
                        overall_rating: 0,
                        comments: '',
                        success_indicators: [],
                        issues_encountered: [],
                        what_worked_well: [],
                        what_to_improve: [],
                        next_time_notes: ''
                    },
                    metrics: {
                        total_cook_time_seconds: 0,
                        prep_time_seconds: 0,
                        active_cook_time_seconds: 0,
                        rest_time_seconds: 0,
                        steps_completed: 0,
                        steps_skipped: 0,
                        steps_modified: 0,
                        perceived_difficulty: 3,
                        complexity_factors: [],
                        expected_servings: null,
                        actual_servings: null,
                        equipment_used: [],
                        ingredients_used: [],
                        substitutions_made: []
                    },
                    chef_data: {
                        session_summary: null,
                        key_insights: [],
                        improvement_suggestions: [],
                        reminders_shown: 0,
                        reminders_responded_to: 0,
                        photo_reminders_accepted: 0,
                        checkin_responses: 0,
                        voice_commands_used: [],
                        voice_accuracy_score: null,
                        ai_notes: [],
                        ai_photos_analyzed: [],
                        ai_suggestions_given: []
                    },
                    version: '2.0.0'
                };
            } else {
                throw error;
            }
        }
        
        // Ensure all required fields are present
        if (!currentReport.session_id) currentReport.session_id = sessionId;
        if (!currentReport.user_id) currentReport.user_id = user.uid;
        if (!currentReport.start_time) currentReport.start_time = new Date().toISOString();
        if (!currentReport.timeline) currentReport.timeline = [];
        if (!currentReport.highlights) currentReport.highlights = [];
        if (!currentReport.final_outcome) {
            currentReport.final_outcome = {
                overall_rating: 0,
                comments: '',
                success_indicators: [],
                issues_encountered: [],
                what_worked_well: [],
                what_to_improve: [],
                next_time_notes: ''
            };
        }
        if (!currentReport.metrics) {
            currentReport.metrics = {
                total_cook_time_seconds: 0,
                prep_time_seconds: 0,
                active_cook_time_seconds: 0,
                rest_time_seconds: 0,
                steps_completed: 0,
                steps_skipped: 0,
                steps_modified: 0,
                perceived_difficulty: 3,
                complexity_factors: [],
                expected_servings: null,
                actual_servings: null,
                equipment_used: [],
                ingredients_used: [],
                substitutions_made: []
            };
        }
        if (!currentReport.chef_data) {
            currentReport.chef_data = {
                session_summary: null,
                key_insights: [],
                improvement_suggestions: [],
                reminders_shown: 0,
                reminders_responded_to: 0,
                photo_reminders_accepted: 0,
                checkin_responses: 0,
                voice_commands_used: [],
                voice_accuracy_score: null,
                ai_notes: [],
                ai_photos_analyzed: [],
                ai_suggestions_given: []
            };
        }
        
        // Update timeline entries with step reflections
        const updatedTimeline = [...(currentReport.timeline || [])];
        stepReflections.forEach((reflection, stepIndex) => {
            // Find or create timeline entry for this step
            let timelineEntry = updatedTimeline.find(entry => entry.step_index === stepIndex);
            if (!timelineEntry) {
                timelineEntry = {
                    id: `step_${stepIndex}_${Date.now()}`,
                    timestamp: new Date().toISOString(),
                    phase: 'active',
                    step_index: stepIndex,
                    step_title: steps[stepIndex]?.title || `Step ${stepIndex + 1}`,
                    notes: '',
                    photos: [],
                    status: 'ok'
                };
                updatedTimeline.push(timelineEntry);
            }
            
            // Ensure required timeline entry fields
            if (!timelineEntry.timestamp) timelineEntry.timestamp = new Date().toISOString();
            if (!timelineEntry.phase) timelineEntry.phase = 'active';
            if (!timelineEntry.status) timelineEntry.status = 'ok';
            
            // Update with reflection data
            if (reflection.notes) {
                timelineEntry.notes = reflection.notes;
            }
            if (reflection.photos && reflection.photos.length > 0) {
                timelineEntry.photos = reflection.photos.map(photo => ({
                    url: photo.url || photo.base64 || '',
                    timestamp: new Date().toISOString(),
                    caption: '',
                    trigger: 'reflection'
                }));
            }
        });
        
        // Update final outcome
        const updatedReport = {
            ...currentReport,
            timeline: updatedTimeline,
            final_outcome: {
                ...currentReport.final_outcome,
                comments: finalThoughts || currentReport.final_outcome.comments || '',
                dish_photo: finalPhoto ? {
                    url: finalPhoto.url || finalPhoto.base64 || '',
                    timestamp: new Date().toISOString(),
                    caption: 'Final dish',
                    trigger: 'reflection'
                } : currentReport.final_outcome?.dish_photo
            },
            updated_at: new Date().toISOString()
        };
        
        await sessionReportService.updateSessionReport(sessionId, updatedReport, token);
        
        // If this is a recipe session and recipe has no images, add final photo to recipe
        // CRITICAL: Only update the SPECIFIC recipe from the session, not any other recipes in the lineage
        if (isRecipe && recipeId && finalPhoto) {
            try {
                // Use url if available, otherwise use base64 (shouldn't happen but just in case)
                const photoUrl = finalPhoto.url || finalPhoto.base64;
                if (!photoUrl) {
                    console.warn('Final photo has no URL or base64');
                    return;
                }
                
                // Store the specific recipe ID to ensure we only update this one
                // Normalize to number for comparison (recipe IDs are numbers in the database)
                const specificRecipeId = typeof recipeId === 'string' ? parseInt(recipeId, 10) : recipeId;
                
                const currentRecipe = await getRecipeById(specificRecipeId);
                
                // Verify we got the correct recipe (normalize both IDs for comparison)
                const currentRecipeId = typeof currentRecipe.id === 'string' ? parseInt(currentRecipe.id, 10) : currentRecipe.id;
                const normalizedSpecificId = typeof specificRecipeId === 'string' ? parseInt(specificRecipeId, 10) : specificRecipeId;
                
                if (currentRecipeId !== normalizedSpecificId) {
                    console.error('❌ ReflectionModal: Recipe ID mismatch!', {
                        expected: normalizedSpecificId,
                        expectedType: typeof normalizedSpecificId,
                        received: currentRecipeId,
                        receivedType: typeof currentRecipeId,
                        originalRecipeId: recipeId,
                        originalType: typeof recipeId
                    });
                    return; // Don't update if IDs don't match
                }
                
                console.log('🔍 ReflectionModal: Checking recipe for image update:', {
                    sessionId,
                    recipeId: specificRecipeId,
                    recipeName: currentRecipe.name,
                    chain_root_id: currentRecipe.chain_root_id,
                    parent_recipe_id: currentRecipe.parent_recipe_id,
                    hasImages: !!(currentRecipe.images && currentRecipe.images.length > 0),
                    imageCount: currentRecipe.images?.length || 0,
                    finalPhotoUrl: photoUrl
                });
                
                if (!currentRecipe.images || currentRecipe.images.length === 0) {
                    const formDataForUpdate = new FormData();
                    
                    // Add all existing recipe fields
                    formDataForUpdate.append('name', currentRecipe.name || '');
                    formDataForUpdate.append('category', currentRecipe.category || '');
                    if (currentRecipe.cooking_time) {
                        formDataForUpdate.append('cooking_time', currentRecipe.cooking_time);
                    }
                    
                    // Add the final photo URL as existingImages (backend expects URLs in existingImages, not images)
                    formDataForUpdate.append('existingImages', JSON.stringify([photoUrl]));
                    
                    console.log('🔍 ReflectionModal: Updating SPECIFIC recipe with final photo:', {
                        sessionId,
                        recipeId: specificRecipeId,
                        recipeName: currentRecipe.name,
                        photoUrl
                    });
                    
                    // Update ONLY this specific recipe by ID
                    await updateRecipe(specificRecipeId, formDataForUpdate, token);
                    
                    console.log('✅ ReflectionModal: Recipe updated successfully with final photo:', {
                        recipeId: specificRecipeId
                    });
                } else {
                    console.log('ℹ️ ReflectionModal: Recipe already has images, skipping update:', {
                        recipeId: specificRecipeId,
                        imageCount: currentRecipe.images.length
                    });
                }
            } catch (error) {
                console.error('❌ ReflectionModal: Error adding final photo to recipe:', error);
                // Don't fail the whole operation if recipe update fails
            }
        } else {
            console.log('🔍 ReflectionModal: Skipping recipe update:', {
                isRecipe,
                recipeId,
                hasFinalPhoto: !!finalPhoto,
                finalPhotoUrl: finalPhoto?.url
            });
        }
    };

    const handleFinishNoPost = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        
        try {
            // Save reflection data to session report
            await saveReflectionData();
            
            onFinishNoPost();
            onClose();
        } catch (error) {
            console.error('Error saving reflection:', error);
            alert('Failed to save reflection. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePost = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        
        try {
            // Save reflection data
            await saveReflectionData();
            
            onClose();
            onPost();
        } catch (error) {
            console.error('Error saving reflection for post:', error);
            alert('Failed to save reflection. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            maxWidth="max-w-6xl"
        >
            <div className="space-y-6">
                <Heading level={1} className="text-neutral-900 text-3xl font-mono uppercase tracking-wider mb-6">
                    Cooking Reflection
                </Heading>
                {/* Steps - Horizontal Layout */}
                <div>
                    <Heading level={3} className="text-neutral-900 text-xl font-mono uppercase tracking-wider mb-4">
                        Steps
                    </Heading>
                    <div className="flex gap-4 overflow-x-auto pb-4">
                        {steps.map((step, index) => (
                            <div 
                                key={index} 
                                className="flex-shrink-0 w-64 bg-neutral-50 hairline p-4"
                            >
                                {/* Step Title - Grayed out small text */}
                                <Text className="text-neutral-900 opacity-40 text-xs font-mono uppercase tracking-wider mb-3">
                                    {step.title || `Step ${index + 1}`}
                                </Text>
                                
                                {/* Step Body - Grayed out small text */}
                                {step.body && (
                                    <Text className="text-neutral-900 opacity-40 text-xs mb-3 leading-relaxed">
                                        {step.body}
                                    </Text>
                                )}
                                
                                {/* Add Photo/Text Buttons */}
                                <div className="flex gap-2 mb-3">
                                    <button
                                        onClick={() => handleAddPhotoToStep(index)}
                                        className="flex-1 px-2 py-1 bg-neutral-0 text-neutral-900 hairline font-mono text-xs uppercase tracking-wider hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
                                    >
                                        + Photo
                                    </button>
                                    <button
                                        onClick={() => handleAddTextToStep(index)}
                                        className="flex-1 px-2 py-1 bg-neutral-0 text-neutral-900 hairline font-mono text-xs uppercase tracking-wider hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
                                    >
                                        + Text
                                    </button>
                                </div>
                                
                                {/* Notes Input (shown when adding text) */}
                                {addingToStep === index ? (
                                    <textarea
                                        value={stepReflections[index].notes}
                                        onChange={(e) => handleStepNotesChange(index, e.target.value)}
                                        onBlur={() => setAddingToStep(null)}
                                        placeholder="Add your notes..."
                                        className="w-full p-2 text-sm bg-neutral-0 text-neutral-900 hairline font-sans resize-none"
                                        rows={3}
                                        autoFocus
                                    />
                                ) : stepReflections[index].notes ? (
                                    <Text className="text-neutral-900 text-xs mb-2">
                                        {stepReflections[index].notes}
                                    </Text>
                                ) : null}
                                
                                {/* Photos Display */}
                                {stepReflections[index].photos && stepReflections[index].photos.length > 0 && (
                                    <div className="space-y-2">
                                        {stepReflections[index].photos.map((photo, photoIndex) => (
                                            <img
                                                key={photoIndex}
                                                src={photo.url || photo.base64}
                                                alt={`Step ${index + 1} photo ${photoIndex + 1}`}
                                                className="w-full h-32 object-cover hairline"
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                
                {/* Final Thoughts */}
                <div>
                    <Heading level={3} className="text-neutral-900 text-xl font-mono uppercase tracking-wider mb-4">
                        Final Thoughts
                    </Heading>
                    <textarea
                        value={finalThoughts}
                        onChange={(e) => setFinalThoughts(e.target.value)}
                        placeholder="Share your thoughts about the cooking experience..."
                        className="w-full p-4 text-base bg-neutral-0 text-neutral-900 hairline font-sans resize-none"
                        rows={4}
                    />
                </div>
                
                {/* Final Photo */}
                <div>
                    <Heading level={3} className="text-neutral-900 text-xl font-mono uppercase tracking-wider mb-4">
                        Final Photo
                    </Heading>
                    {finalPhoto ? (
                        <div className="relative">
                            <img
                                src={finalPhoto.previewUrl || finalPhoto.base64}
                                alt="Final dish"
                                className="w-full max-w-md h-64 object-cover hairline"
                            />
                            <button
                                onClick={handleRemoveFinalPhoto}
                                className="absolute top-2 right-2 w-8 h-8 bg-neutral-900 text-neutral-0 flex items-center justify-center hover:opacity-60 transition-opacity"
                            >
                                ×
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={handleFinalPhotoCapture}
                            className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
                        >
                            + Add Final Photo
                        </button>
                    )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-neutral-900">
                    <button
                        onClick={handleSkip}
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors disabled:opacity-30"
                    >
                        Skip Reflection
                    </button>
                    <button
                        onClick={handleFinishNoPost}
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-30"
                    >
                        Finish w/ no post
                    </button>
                    <button
                        onClick={handlePost}
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-30"
                    >
                        Post
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ReflectionModal;

